/**
 * Subscription Upgrade Page
 * Allows users to upgrade/purchase subscription
 */

import React, {useState, useEffect} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {CreditCard, ArrowLeft, CheckCircle} from 'lucide-react';
import PaymentCheckout from '../components/subscription/PaymentCheckout';
import SubscriptionLayout from '../components/subscription/SubscriptionLayout';
import {useAuth} from '../contexts/AuthContext';
import {getSubscription, PlanType, Subscription} from '../services/SubscriptionService';

const SubscriptionUpgradePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {user} = useAuth();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const orgId = searchParams.get('org') || user?.organizationId || null;
    
    if (orgId) {
      setOrganizationId(orgId);
      loadSubscription(orgId);
    } else {
      navigate('/');
    }
  }, [searchParams, user, navigate]);

  const loadSubscription = async (orgId: string) => {
    setIsLoading(true);
    try {
      const sub = await getSubscription(orgId);
      setSubscription(sub);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanSelect = (planType: PlanType) => {
    setSelectedPlan(planType);
    setShowCheckout(true);
  };

  const plans: Array<{
    id: PlanType;
    name: string;
    price: string;
    description: string;
    features: string[];
  }> = [
    {
      id: 'individual',
      name: 'Individual',
      price: '$5',
      description: 'Perfect for personal use',
      features: ['Up to 5 users', 'Basic challenges', 'Activity tracking'],
    },
    {
      id: 'starter',
      name: 'Starter',
      price: '$5/user',
      description: 'Ideal for small teams',
      features: ['Up to 100 users', 'All challenge types', 'Leaderboards', 'Email support'],
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$8/user',
      description: 'For growing organizations',
      features: [
        'Up to 500 users',
        'All features',
        'Custom challenges',
        'Analytics',
        'Priority support',
      ],
    },
  ];

  if (!organizationId) {
    return null;
  }

  if (showCheckout && selectedPlan) {
    return (
      <SubscriptionLayout>
        <PaymentCheckout
          organizationId={organizationId}
          planType={selectedPlan}
          onSuccess={() => navigate('/subscription/success')}
          onCancel={() => setShowCheckout(false)}
        />
      </SubscriptionLayout>
    );
  }

  if (isLoading) {
    return (
      <SubscriptionLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </SubscriptionLayout>
    );
  }

  return (
    <SubscriptionLayout>
      <div className="max-w-6xl mx-auto">

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Select the plan that best fits your organization
          </p>
          {subscription && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Current plan: <span className="font-semibold capitalize">{subscription.planType}</span>
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map(plan => {
            const isCurrentPlan = subscription?.planType === plan.id;
            
            return (
              <div
                key={plan.id}
                className={`relative bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 border-2 ${
                  isCurrentPlan
                    ? 'border-purple-500 dark:border-purple-400'
                    : 'border-gray-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600'
                }`}
              >
                {isCurrentPlan && (
                  <div className="absolute top-0 left-0 right-0 bg-purple-600 text-white text-center py-2 text-sm font-semibold rounded-t-xl">
                    Current Plan
                  </div>
                )}
                <div className={isCurrentPlan ? 'mt-8' : ''}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {plan.name}
                    </h3>
                    <span className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                      {plan.price}
                    </span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">{plan.description}</p>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handlePlanSelect(plan.id)}
                    disabled={isCurrentPlan}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                      isCurrentPlan
                        ? 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                    }`}
                  >
                    {isCurrentPlan ? 'Current Plan' : 'Select Plan'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SubscriptionLayout>
  );
};

export default SubscriptionUpgradePage;

